// submitRiskCheckSync.js
'use strict';
import fetch from '../resource/customFetch.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import handleFetchError from '../resource/handleFetchError.js';
import getMeData from '../selector/getMeData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getTranslationKey from '../resource/getTranslationKey.js';
import {
  FINGERPRINTJS_PRO_API_KEY,
  FINGERPRINTJS_PRO_ENDPOINT,
  FINGERPRINTJS_PRO_REGION,
} from '../RemoteConfigKeys.js';
import {
  MERGE_OPERATION_DATA,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
} from '../ActionTypes.js';

const TIMEOUT_MS = 5000;
const NETWORKING_SELECT_PATH = ['risk-check'];

const raceWithTimeout = ({ promise, label }) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(
      () => reject(new Error(`${label}_timeout_${TIMEOUT_MS}ms`)),
      TIMEOUT_MS
    );
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

/**
 * Synchronous risk check via POST /risk-check (FingerprintJS only, no GeeTest).
 * Writes riskCheckState and riskCheckErrorKey to operation. Both 'blocked' and
 * 'error' block checkout (see checkRiskGate).
 *
 *   collect requestId (FingerprintJS) ─► POST /risk-check ─┬─ 'pass'    ──► riskCheckState 'pass'
 *                                                          ├─ 'blocked' ──► riskCheckState 'blocked'
 *                                                          └─ error / non-200 ──► riskCheckState 'error' (+ errorKey)
 *
 * @see https://api.docs.swag.live/#tag/security/operation/risk_check__post
 * @kind action
 * @param {string} {triggeredType} - trigger scenario, e.g. 'STORE'.
 * @return {Promise} Action promise.
 */
const submitRiskCheckSync =
  ({ triggeredType }) =>
  async (dispatch, getState) => {
    const state = getState();
    const token = getMeData(state, 'token');
    if (!token) return dispatch({ type: '' });

    // Step 1: Collect a FingerprintJS Pro requestId (with timeout and error capture).
    let requestId = null;
    let fingerprintError = null;
    const fpJsAPIKey = getRemoteConfigData(state, FINGERPRINTJS_PRO_API_KEY);
    if (!fpJsAPIKey) {
      fingerprintError = 'remote_config_fp_pro_api_key_missing';
    } else {
      try {
        const userId = getMeData(state, 'id');
        const clientId = getMeData(state, 'clientId');
        const fpJsEndpoint = getRemoteConfigData(
          state,
          FINGERPRINTJS_PRO_ENDPOINT
        );
        const fpJsRegion = getRemoteConfigData(state, FINGERPRINTJS_PRO_REGION);
        const { getFpProRequestId } = await import('../resource/fpjs.js');
        const { requestId: fetchedRequestId, visitorId } =
          await raceWithTimeout({
            promise: getFpProRequestId({
              fpJsAPIKey,
              fpJsEndpoint,
              fpJsRegion,
              clientId,
              userId,
            }),
            label: 'fp_pro_collect',
          });
        requestId = fetchedRequestId || null;
        if (!requestId) {
          fingerprintError = 'fp_pro_request_id_empty_after_success';
        }
        // Cache the visitorId from this same fpPro.get() so the risk gate's
        // getFpIdPro can reuse it instead of calling the FingerprintJS API again.
        if (visitorId) {
          const { setItem } = await import('../resource/persist.js');
          await setItem('_fpId', visitorId);
        }
      } catch (error) {
        fingerprintError = error?.message || 'fp_pro_error_without_message';
      }
    }

    const fetchUrl = getResourceUrl({ endpoint: '/risk-check' });
    const fetchOptions = {
      method: 'POST',
      headers: {
        ...getHeaders(),
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        fingerprint: {
          request_id: requestId,
          error: fingerprintError,
        },
        triggered_type: triggeredType,
      }),
    };

    // Step 2: Send the request and parse riskCheckState / errorKey.
    let riskCheckState = 'error';
    let errorKey = '';
    dispatch({
      type: SET_NETWORKING_FETCHING,
      payload: { selectPath: NETWORKING_SELECT_PATH },
    });
    try {
      let response = await fetch(fetchUrl.href, fetchOptions);
      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl,
        });
      }
      const data = await response.json();
      riskCheckState = data?.state || 'error';
      dispatch({
        type: SET_NETWORKING_SUCCESS,
        payload: { selectPath: NETWORKING_SELECT_PATH },
      });
    } catch (error) {
      dispatch({
        type: SET_NETWORKING_ERROR,
        payload: { selectPath: NETWORKING_SELECT_PATH, error },
      });
      try {
        errorKey = getTranslationKey({ key: JSON.parse(error.message).code });
        // eslint-disable-next-line no-empty
      } catch (_) {}
    }

    return dispatch({
      type: MERGE_OPERATION_DATA,
      payload: {
        selectPath: ['fingerprint'],
        data: { riskCheckState, riskCheckErrorKey: errorKey || '' },
      },
    });
  };

export default submitRiskCheckSync;
